import Dexie, { type Table } from 'dexie';
import type { StoredDownloadJob } from '../@types/repository-groups';

import type { RecipeDetailsFragment } from '../__generated__/apollo-hooks';

export type GraphNodeData = {
  /**
   * This is not the recipe ID.This is a node Id which is more of an index
   * assigned when we recurse through the list of recipes
   */
  id: number;
  name: string;
  /**
   * Recipe descriptor data. The type is `RecipeDetailsFragment`, but the
   * shape is NOT always fully populated:
   *   - Nested recipe-list path (`transformNestedRecipeToNodesAndLinks`,
   *     legacy fragment fetches): close-to-full RecipeDetailsFragment.
   *   - v2 graph path (`transformGraphToNodesAndLinks`): thin
   *     `BuilderRecipeNodeFields` only — id, instanceName, name, options.
   *     bundle, description, recipeCount, dataTables, detail are absent.
   * Consumers should use optional chaining and tolerate missing values.
   * The type itself is wider than the runtime guarantees; widening to
   * `Partial<RecipeDetailsFragment>` cascaded type errors into many
   * downstream consumers that require specific fields.
   */
  val?: RecipeDetailsFragment;
  isCustomRecipe?: boolean;
  isMissingRecipe?: boolean;
  isPrecondition?: boolean;
  /**
   * True when this node is a pointer to another node in the same recipe
   * (server-side Singleton dedup via RecipeGraphReferenceVertex). References
   * are not editable and cannot be expanded — the tree renders them as a
   * dimmed chip. `referencesNodeId` points at the GraphNodeData.id of the
   * target the reference resolves to.
   */
  isReference?: boolean;
  referencesNodeId?: number;
};

export type GraphLink = {
  /**
   * references a `GraphNodeData.id`
   */
  source: number;
  /**
   * references a `GraphNodeData.id`
   */
  target: number;
};

export type BuilderRecipe = {
  id: string;
  name: string;
  description?: string;
  nodes: GraphNodeData[];
  links: GraphLink[];
};

export type RecentRecipeDBEntry = {
  id: string;
  name: string;
  description: string;
  createdAt: number;
};

export type ExcludedFilesEntry = {
  id: string; // Composite key: `${recipeRunId}:${repositoryId}`
  excludedFiles: string[];
};

class ModerneIndexedDb extends Dexie {
  downloadJobs: Table<StoredDownloadJob>;
  builderRecipes: Table<BuilderRecipe>;
  recentRecipes: Table<RecentRecipeDBEntry>;
  excludedFiles: Table<ExcludedFilesEntry>;

  constructor() {
    super('moderne-v2');
    // Versions 1-4, 6, 10 previously defined stores that are no longer used
    // (repositoryGroups, organizations, jobs, tasks). Declarations are
    // retained for upgrade path compatibility — Dexie's deleteRemovedTables
    // automatically drops stores absent from the cumulative schema.
    this.version(1).stores({});
    this.version(2).stores({});
    this.version(3).stores({});
    this.version(4).stores({});
    this.version(5).stores({
      downloadJobs: 'id,manifest'
    });
    this.version(6).stores({});
    this.version(7).stores({
      builderRecipes: 'id,name,nodes,links'
    });
    this.version(8).stores({
      recentRecipes: 'id,name,description,createdAt'
    });
    this.version(10).stores({});
    this.version(11).stores({
      excludedFiles: 'id'
    });
  }
}

export const db = new ModerneIndexedDb();
